import { useEffect, useRef, useState } from 'react';

interface ServicesProps {
  scrollY: number;
}

export default function Services({ scrollY }: ServicesProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-form');
    if (contactSection) {
      contactSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: 'ri-hammer-line',
      title: 'Specialized Construction',
      description: 'Expert construction services tailored to your unique project requirements with precision and quality craftsmanship.',
      image: 'https://public.readdy.ai/ai/img_res/7f8313a4fd6e2f58814d958c8b8d4f2b.jpg',
      features: ['Custom Building', 'Structural Work', 'Quality Materials', 'Expert Team']
    },
    {
      icon: 'ri-tools-line',
      title: 'Equipment Rental',
      description: 'Access to premium construction equipment and machinery for projects of any scale with flexible rental terms.',
      image: 'https://readdy.ai/api/search-image?query=Attractive%20woman%20construction%20supervisor%20in%20safety%20gear%20standing%20next%20to%20heavy%20construction%20machinery%20excavator%20and%20equipment%20modern%20construction%20site%20sunny%20day%20professional%20attire%20simple%20clean%20background%20industrial%20setting%20high%20quality%20photography%20realistic%20detailed&width=800&height=600&seq=service-equipment-diverse-002&orientation=landscape',
      features: ['Heavy Machinery', 'Power Tools', 'Safety Equipment', 'Flexible Terms']
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Renovation Services',
      description: 'Transform your space with our comprehensive renovation solutions from concept to completion.',
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20woman%20architect%20in%20professional%20attire%20holding%20renovation%20plans%20and%20paint%20samples%20modern%20renovated%20interior%20space%20bright%20natural%20lighting%20confident%20professional%20smile%20clean%20minimalist%20background%20interior%20design%20elements%20high%20quality%20photography%20realistic%20detailed&width=800&height=600&seq=service-renovation-diverse-003&orientation=landscape',
      features: ['Interior Design', 'Modernization', 'Space Planning', 'Quality Finishes']
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Consulting Services',
      description: 'Professional guidance and expert advice for your construction projects from planning to execution.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20woman%20construction%20consultant%20in%20business%20attire%20reviewing%20architectural%20plans%20and%20documents%20modern%20office%20setting%20natural%20lighting%20confident%20smile%20clean%20simple%20background%20laptop%20and%20blueprints%20on%20desk%20high%20quality%20photography%20realistic%20detailed%20professional&width=800&height=600&seq=service-consulting-diverse-004&orientation=landscape',
      features: ['Project Planning', 'Cost Analysis', 'Risk Management', 'Technical Support']
    },
  ];

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative py-32 bg-gradient-to-b from-white to-slate-50 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-200 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-20">
          <div
            className={`inline-block mb-4 px-6 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-full transform transition-all duration-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <span className="text-blue-600 font-semibold">Our Services</span>
          </div>
          
          <h2
            className={`text-5xl md:text-6xl font-bold mb-6 transform transition-all duration-1000 delay-100 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <span className="bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent">
              Excellence in Every Project
            </span>
          </h2>
          
          <p
            className={`text-xl text-gray-600 max-w-3xl mx-auto transform transition-all duration-1000 delay-200 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            Comprehensive construction solutions tailored to your needs, delivered with precision and innovation
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group relative bg-white rounded-3xl overflow-hidden shadow-lg shadow-gray-200/50 hover:shadow-2xl hover:shadow-gray-300/50 transform transition-all duration-500 hover:-translate-y-2 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-20 group-hover:opacity-30 transition-opacity duration-500`}></div>
              </div>

              <div className="p-8">
                <div className={`w-16 h-16 mb-6 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                  {service.title}
                </h3>

                <p className="text-gray-600 leading-relaxed mb-6">
                  {service.description}
                </p>

                <button 
                  onClick={scrollToContact}
                  className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-orange-500/50 transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Contact Us
                  <i className="ri-arrow-right-line ml-2 transform group-hover:translate-x-2 transition-transform duration-300"></i>
                </button>
              </div>

              <div className={`absolute top-4 right-4 w-24 h-24 bg-gradient-to-br ${service.color} rounded-full blur-2xl opacity-0 group-hover:opacity-30 transition-opacity duration-500`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
